import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Upload, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const DocumentUploader = ({ onDocumentProcessed }) => {
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploadedFile(file);
    setUploading(true);
    setError(null);
    setProgress(0);

    try {
      // Upload file
      const formData = new FormData();
      formData.append('file', file);

      setProgress(25);
      const uploadResponse = await axios.post(`${process.env.REACT_APP_API_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const documentId = uploadResponse.data.document_id;
      setProgress(50);
      setUploading(false);
      setProcessing(true);

      // Process document into chunks and embeddings
      const processResponse = await axios.post(`${process.env.REACT_APP_API_URL}/process/${documentId}`);
      setProgress(75);

      // Generate embeddings
      await axios.post(`${process.env.REACT_APP_API_URL}/generate-embeddings/${documentId}`);
      setProgress(100);

      setProcessing(false);
      onDocumentProcessed(documentId);

    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred during upload/processing');
      setUploading(false);
      setProcessing(false);
      setProgress(0);
    }
  }, [onDocumentProcessed]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
    },
    maxFiles: 1,
    disabled: uploading || processing
  });

  const getStatusMessage = () => {
    if (uploading) return 'Uploading document...';
    if (processing && progress < 75) return 'Processing document into chunks...';
    if (processing && progress >= 75) return 'Generating embeddings...';
    return '';
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 cursor-pointer
          ${isDragActive 
            ? 'border-primary-400 bg-primary-50' 
            : uploading || processing 
              ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50'
          }
        `}
      >
        <input {...getInputProps()} />
        
        {!uploading && !processing && (
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              <Upload className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {isDragActive ? 'Drop your document here' : 'Upload your document'}
              </h3>
              <p className="text-gray-600">
                Drag and drop a PDF, TXT, or MD file here, or click to select
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Supported formats: PDF, TXT, MD (Max 50MB)
              </p>
            </div>
          </div>
        )}

        {(uploading || processing) && (
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {getStatusMessage()}
              </h3>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">{progress}% complete</p>
              {uploadedFile && (
                <div className="flex items-center justify-center mt-4 p-3 bg-white rounded-lg border">
                  <FileText className="w-5 h-5 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-700">{uploadedFile.name}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-slide-up">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
            <div>
              <h4 className="text-sm font-semibold text-red-800">Upload Failed</h4>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={() => {
              setError(null);
              setUploadedFile(null);
              setProgress(0);
            }}
            className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Try again
          </button>
        </div>
      )}

      {/* Processing Steps */}
      <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">What happens after upload:</h4>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-primary-600">1</span>
            </div>
            <span className="text-gray-700">Document is parsed and extracted</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-primary-600">2</span>
            </div>
            <span className="text-gray-700">Content is split into semantic chunks</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-primary-600">3</span>
            </div>
            <span className="text-gray-700">AI embeddings are generated for each chunk</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-xs font-semibold text-primary-600">4</span>
            </div>
            <span className="text-gray-700">Search interface becomes available</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploader; 